<script setup lang="ts">
/**
 * Reusable checkbox group for the multi-step form (steps 2-3).
 *
 * Props:
 * - `options` — list of { label, value }
 * - `legend` — fieldset heading text
 * - `multiple` — true = multi-select (model: string[]), false = single-select
 *   with radio-like behavior (model: string, square checkboxes)
 *
 * Single-select mode: selecting another option replaces the current one;
 * clicking the already-selected option keeps it (radio behavior, per spec).
 */
const props = defineProps<{
  options: { label: string; value: string }[]
  legend: string
  multiple?: boolean
}>()

const model = defineModel<string[] | string>({
  default: () => [],
})

function isChecked(value: string): boolean {
  if (props.multiple) {
    return (model.value as string[]).includes(value)
  }
  return model.value === value
}

function toggle(value: string) {
  if (props.multiple) {
    const current = [...(model.value as string[])]
    const idx = current.indexOf(value)
    if (idx >= 0) current.splice(idx, 1)
    else current.push(value)
    model.value = current
    return
  }
  if (model.value === value) return
  model.value = value
}
</script>

<template>
  <fieldset>
    <legend class="mb-4 font-semibold text-text">{{ legend }}</legend>
    <div class="flex flex-col gap-3">
      <label
        v-for="opt in options"
        :key="opt.value"
        class="flex cursor-pointer items-center gap-3 rounded-[10px] border bg-white px-4 py-3 transition-colors active:scale-[0.99]"
        :class="
          isChecked(opt.value)
            ? 'border-coral bg-coral-light/40'
            : 'border-border'
        "
      >
        <input
          type="checkbox"
          :checked="isChecked(opt.value)"
          class="h-[18px] w-[18px] shrink-0 cursor-pointer rounded accent-coral"
          @change="toggle(opt.value)"
        />
        <span class="text-sm text-text">{{ opt.label }}</span>
      </label>
    </div>
  </fieldset>
</template>
